import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Microphone, House, Storefront, CheckCircle } from "@phosphor-icons/react";

function Choice({ active, onClick, Icon, title, desc, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`press w-full text-left p-4 rounded-xl border-2 border-black flex items-start gap-3 transition-colors ${active ? "bg-primary text-white" : "bg-white"}`}
    >
      <div className={`w-10 h-10 rounded-lg border-2 border-black flex items-center justify-center shrink-0 ${active ? "bg-white text-primary" : "bg-[#F3F4F6]"}`}><Icon size={20} weight="bold" /></div>
      <div className="flex-1">
        <p className="font-bold text-sm flex items-center gap-1.5">{title} {active && <CheckCircle size={16} weight="fill" />}</p>
        <p className={`text-xs mt-0.5 leading-snug ${active ? "text-white/80" : "text-black/50"}`}>{desc}</p>
      </div>
    </button>
  );
}

export default function FirstTimeSetup({ onDone }) {
  const { user, savePrefs } = useAuth();
  const [screen, setScreen] = useState("dashboard");
  const [busy, setBusy] = useState(false);

  const finish = async () => {
    setBusy(true);
    try {
      await savePrefs({
        first_time_setup_complete: true,
        default_opening_screen: screen,
        voice_capture_on_startup: screen === "voice",
        voice_feature_enabled: true,
      });
    } finally { setBusy(false); onDone?.(); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        data-testid="first-time-setup"
        className="relative bg-background border-2 border-black rounded-2xl hard-shadow w-full max-w-md overflow-hidden"
      >
        <div className="bg-[#E0F2FE] border-b-2 border-black px-6 py-5">
          <div className="w-11 h-11 rounded-xl bg-primary border-2 border-black flex items-center justify-center mb-3"><Storefront size={24} weight="fill" color="#fff" /></div>
          <h2 className="font-display text-2xl font-black tracking-tighter">Welcome, {user.name?.split(" ")[0]}!</h2>
          <p className="text-sm text-black/60 mt-1">Pick the screen you'd like to see first each time you open Short Stack Hawai'i. You can change this anytime in Settings.</p>
        </div>
        <div className="p-6 space-y-3">
          <Choice testid="setup-choice-dashboard" active={screen === "dashboard"} onClick={() => setScreen("dashboard")} Icon={House} title="Task Dashboard" desc="Start with your tasks, stats and reminders." />
          <Choice testid="setup-choice-voice" active={screen === "voice"} onClick={() => setScreen("voice")} Icon={Microphone} title="Voice Capture" desc="Jump straight into voice-first task capture." />
          <button data-testid="setup-finish-btn" onClick={finish} disabled={busy} className="press w-full h-12 mt-2 rounded-full bg-secondary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60">
            {busy ? "Setting up…" : "Get started"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
